import * as Icon from "@ant-design/icons";
import { Button, Modal, Col, Form, Select, Row, Image, DatePicker, DatePickerProps, Tooltip, Flex } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";
import { useState, useEffect } from "react";
import { ConfiguracaoImpostoAdicionalModel } from "../../models/configuracaoImpostoAdicional/configuracaoImpostoAdicionalModel";
import { deletarConfiguracaoImpostoAdicional, listarConfiguracaoImpostoAdicional } from "../../services/configuracaoImpostoAdicionalService";
import { useAppSelector, useAppDispatch } from "../../util/hooks";
import NovoConfiguracaoImpostoAdicional from "./modals/cadastrar";
import { setIdEmpresaPermissao, setTitle } from "../../redux/slices/configuracoes/configuracoesSlice";
import {
  setModalCadastrarConfiguracaoImpostoAdicional,
  setBuscaConfiguracaoImpostoAdicional,
  setEntityConfiguracaoImpostoAdicional,
  setModalDeletarConfiguracaoImpostoAdicional,
} from "../../redux/slices/configuracaoImpostoAdicional/configuracaoImpostoAdicionalSlice";
import { buscarEmpresa } from "../../services/empresaService";
import dayjs from "dayjs";
import { BuscaConfiguracaoImpostoAdicionalModel } from "../../models/configuracaoImpostoAdicional/buscaConfiguracaoImpostoAdicionalModel";
import { ItemMenuAcoes } from "../../models/sistema/itemMenuAcoes";
import AcoesComponent from "../../components/table/acoesComponent";
import { EmpresaModel } from "../../models/empresa/empresaModel";
import { ButtonComponent } from "../../components/button/cadastrar";
import TableComponent from "../../components/table/tableComponent";
import MensagemModal from "../../components/modal/mensagem";

dayjs.locale("pt-br");

const ConfiguracaoImpostoAdicional = () => {
  //estados globais
  const configuracaoImpostoAdicionalState = useAppSelector((state) => state.configuracaoImpostoAdicional);
  const empresaState = useAppSelector((state) => state.empresa);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<any>();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null | string>>({});

  const editar = Object.values(configuracaoImpostoAdicionalState.entity).length !== 0;

  useEffect(() => {
    dispatch(setTitle("CONFIGURAÇÃO DE IMPOSTO ADICIONAL"));
    dispatch(buscarEmpresa(empresaState.busca));
    form.setFieldsValue({ dataInicio: dayjs().startOf("year"), dataFim: dayjs().endOf("year") });
    dispatch(listarConfiguracaoImpostoAdicional(configuracaoImpostoAdicionalState.busca));
  }, []);

  const goCadastrar = () => {
    dispatch(setEntityConfiguracaoImpostoAdicional({}));
    dispatch(setModalCadastrarConfiguracaoImpostoAdicional(true));
  };

  const goEditar = (value: any) => {
    dispatch(setEntityConfiguracaoImpostoAdicional(value));
    dispatch(setModalCadastrarConfiguracaoImpostoAdicional(true));
  };

  const closeModalCadastrar = () => {
    dispatch(setModalCadastrarConfiguracaoImpostoAdicional(false));
  };

  const goDeletar = (value: any) => {
    dispatch(setEntityConfiguracaoImpostoAdicional(value));
    dispatch(listarConfiguracaoImpostoAdicional(configuracaoImpostoAdicionalState.busca));
    dispatch(setModalDeletarConfiguracaoImpostoAdicional(true));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarConfiguracaoImpostoAdicional(false));
  };

  const onChangeEmpresa = (value: number) => {
    dispatch(setIdEmpresaPermissao(value));

    let busca: BuscaConfiguracaoImpostoAdicionalModel = { ...configuracaoImpostoAdicionalState.busca, idEmpresa: value };

    dispatch(setBuscaConfiguracaoImpostoAdicional(busca));
    dispatch(listarConfiguracaoImpostoAdicional(busca));
  };

  const onChangeDateInicio = (value: DatePickerProps["value"], dateString: string | string[]) => {
    let busca: BuscaConfiguracaoImpostoAdicionalModel = { ...configuracaoImpostoAdicionalState.busca, dataInicio: dayjs(value).toDate() };

    dispatch(setBuscaConfiguracaoImpostoAdicional(busca));
    dispatch(listarConfiguracaoImpostoAdicional(busca));
  };

  const onChangeDateFim = (value: DatePickerProps["value"], dateString: string | string[]) => {
    let busca: BuscaConfiguracaoImpostoAdicionalModel = { ...configuracaoImpostoAdicionalState.busca, dataFim: dayjs(value).toDate() };

    dispatch(setBuscaConfiguracaoImpostoAdicional(busca));
    dispatch(listarConfiguracaoImpostoAdicional(busca));
  };

  const onChangeTable: TableProps<ConfiguracaoImpostoAdicionalModel>["onChange"] = (pagination, filters, sorter, extra) => {
    setFilteredInfo(filters);
  };

  const atualizar = () => {
    dispatch(listarConfiguracaoImpostoAdicional(configuracaoImpostoAdicionalState.busca));
  };

  const items: ItemMenuAcoes[] = [
    {
      label: "Editar",
      key: "1",
      icon: <Icon.EditOutlined />,
      function: (item: any) => goEditar(item),
    },
    {
      label: "Remover",
      key: "2",
      icon: <Icon.DeleteOutlined />,
      function: (item: any) => goDeletar(item),
    },
  ];

  const columns: ColumnsType<ConfiguracaoImpostoAdicionalModel> = [
    {
      title: "EMPRESA",
      dataIndex: "apelido",
      width: 120,
      sorter: (a, b) => {
        const apelidoA = a.apelido || "";
        const apelidoB = b.apelido || "";
        return apelidoA.localeCompare(apelidoB);
      },
    },
    {
      title: "MÊS/ANO",
      dataIndex: "mesAno",
      align: "center",
      width: 120,
      sorter: (a, b) => {
        const dateA = new Date(a.mesAno);
        const dateB = new Date(b.mesAno);

        const timeA = dateA.getTime();
        const timeB = dateB.getTime();

        return timeA - timeB;
      },
      render: (_, record) => dayjs(record.mesAno).format("MM/YYYY"),
    },
    {
      title: "QUANTIDADE DE SORTEIO",
      dataIndex: "quantidadeSorteio",
      align: "right",
      width: 120,
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      width: 75,
      render: (_, record) => <AcoesComponent record={record} items={items} />,
    },
  ];

  return (
    <Flex vertical>
      <Form form={form} layout="vertical" onFinish={goCadastrar}>
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              <Form.Item
                label="Empresa"
                name="idEmpresa"
                rules={[
                  {
                    required: true,
                    message: "Selecione uma empresa",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="labelSearch"
                  loading={empresaState.loading}
                  onChange={onChangeEmpresa}
                  style={{ width: 300 }}
                  options={empresaState.list.map((a: EmpresaModel) => ({
                    value: a.id,
                    key: a.apelido,
                    label: (
                      <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                        <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                        {a.apelido}
                      </Flex>
                    ),
                    labelSearch: a.apelido,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Data início" name="dataInicio">
                <DatePicker placeholder="" onChange={onChangeDateInicio} picker="month" format="MM/YYYY" style={{ width: 100 }} />
              </Form.Item>
              <Form.Item label="Data fim" name="dataFim">
                <DatePicker placeholder="" onChange={onChangeDateFim} picker="month" format="MM/YYYY" style={{ width: 100 }} />
              </Form.Item>
              <Form.Item label=" ">
                <Tooltip title="Atualizar">
                  <Button icon={<Icon.RedoOutlined />} className="buttonPrimary" style={{ minHeight: 32 }} onClick={atualizar} />
                </Tooltip>
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex justify="end">
              <ButtonComponent htmlType="submit" />
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent
        data={configuracaoImpostoAdicionalState.list}
        columns={columns}
        loading={configuracaoImpostoAdicionalState.loading}
        tableProps={{
          onChange: onChangeTable,
        }}
      />
      <Modal
        title={editar ? "EDITAR CONFIGURAÇÃO DE IMPOSTO ADICIONAL" : "NOVO CONFIGURAÇÃO DE IMPOSTO ADICIONAL"}
        centered
        width={850}
        open={configuracaoImpostoAdicionalState.modalCadastrar}
        closable={false}
        onCancel={closeModalCadastrar}
        footer={null}
      >
        <NovoConfiguracaoImpostoAdicional />
      </Modal>
      <Modal title={"REMOVER CONFIGURAÇÃO DE IMPOSTO ADICIONAL"} centered width={450} open={configuracaoImpostoAdicionalState.modalDeletar} closable={false} onCancel={closeModalDeletar} footer={null}>
        <MensagemModal
          text="Deseja remover a configuração de imposto adicional?"
          action={deletarConfiguracaoImpostoAdicional({ value: configuracaoImpostoAdicionalState.entity.id, busca: configuracaoImpostoAdicionalState.busca })}
          close={setModalDeletarConfiguracaoImpostoAdicional(false)}
        />
      </Modal>
    </Flex>
  );
};

export default ConfiguracaoImpostoAdicional;
